import { Box } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { FolderInputRow } from '../../molecules/folder-label/FolderInputRow';
import { FolderItemRow } from '../../molecules/folder-label/FolderItemRow';
import type { CompanyMappings, FolderData } from '../../../types/folder-label';

interface FolderTreeItemProps {
    folder: FolderData;
    level?: number;
    onDelete: (id: number) => void;
    onAddSubfolder: (parentId: number, name: string) => void;
    selectedCompany: string;
    companyMappings: CompanyMappings;
}

const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
    folder,
    level = 0,
    onDelete,
    onAddSubfolder,
    selectedCompany,
    companyMappings,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showAddSubfolder, setShowAddSubfolder] = useState(false);
    const [subfolderName, setSubfolderName] = useState('');

    const hasChildren = useMemo(
        () => folder.children && folder.children.length > 0,
        [folder.children]
    );
    const companyMapping = useMemo(
        () => companyMappings[selectedCompany]?.[folder.id],
        [companyMappings, selectedCompany, folder.id]
    );

    const handleAddSubfolder = useCallback(() => {
        if (subfolderName.trim()) {
            onAddSubfolder(folder.id, subfolderName.trim());
            setSubfolderName('');
            setShowAddSubfolder(false);
        }
    }, [subfolderName, onAddSubfolder, folder.id]);

    const handleCancelAdd = useCallback(() => {
        setShowAddSubfolder(false);
        setSubfolderName('');
    }, []);

    const handleToggleExpand = useCallback(() => setIsExpanded((prev) => !prev), []);

    const handleShowAddSubfolder = useCallback(() => {
        setShowAddSubfolder(true);
        setIsExpanded(true);
    }, []);

    const handleDelete = useCallback(() => onDelete(folder.id), [onDelete, folder.id]);

    return (
        <Box>
            <FolderItemRow
                name={folder.name}
                mapping={companyMapping}
                hasChildren={hasChildren || showAddSubfolder}
                isExpanded={isExpanded}
                indentLevel={level}
                onToggleExpand={handleToggleExpand}
                onAddSubfolder={handleShowAddSubfolder}
                onDelete={handleDelete}
            />

            {isExpanded && (
                <Box>
                    {showAddSubfolder && (
                        <FolderInputRow
                            value={subfolderName}
                            onChange={setSubfolderName}
                            onSubmit={handleAddSubfolder}
                            onCancel={handleCancelAdd}
                            placeholder="Enter subfolder name..."
                            indentLevel={level + 1}
                        />
                    )}
                    {hasChildren &&
                        folder.children.map((child) => (
                            <FolderTreeItem
                                key={child.id}
                                folder={child}
                                level={level + 1}
                                onDelete={onDelete}
                                onAddSubfolder={onAddSubfolder}
                                selectedCompany={selectedCompany}
                                companyMappings={companyMappings}
                            />
                        ))}
                </Box>
            )}
        </Box>
    );
};

interface FolderTreeProps {
    folders: FolderData[];
    onDelete: (id: number) => void;
    onAddSubfolder: (parentId: number, name: string) => void;
    selectedCompany: string;
    companyMappings: CompanyMappings;
}

export const FolderTree: React.FC<FolderTreeProps> = ({
    folders,
    onDelete,
    onAddSubfolder,
    selectedCompany,
    companyMappings,
}) => {
    return (
        <Box>
            {folders.map((folder) => (
                <FolderTreeItem
                    key={folder.id}
                    folder={folder}
                    onDelete={onDelete}
                    onAddSubfolder={onAddSubfolder}
                    selectedCompany={selectedCompany}
                    companyMappings={companyMappings}
                />
            ))}
        </Box>
    );
};
